import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Breadcrumbs, Typography, Stack, Button, Box } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import {
  HiPlus,
  HiSearch,
  HiUserAdd,
  HiClipboardList,
  HiUserGroup,
  HiClock,
} from "react-icons/hi";
import "./Breadcrumb.scss";

const Breadcrumb = ({ items }) => {
  const { userResult } = useSelector((state) => state.Auth);
  const id = userResult?.userid;
  const candidateVendorId = localStorage.getItem("candidateVendorId");
  const userInfo = useSelector((state) => state.Profile.result);
  const location = useLocation();
  const [width, setWidth] = useState(() => window.innerWidth);

  const handleResize = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // ---------- Premium Button Style ----------
  const premiumBtn = {
    background: "linear-gradient(135deg, #2c7aa7, #1f5676)",
    textTransform: "none",
    color: "#fff !important",
    fontWeight: 600,
    padding: "6px 14px",
    borderRadius: "8px",
    letterSpacing: "0.5px",
    boxShadow: "0 3px 10px rgba(40,106,148,0.25)",
    transition: "all 0.25s ease",
    "& *": { color: "#fff !important", fill: "#fff !important" },
    "&:hover": {
      background: "linear-gradient(135deg, #368db5, #1a4d68)",
      boxShadow: "0 4px 12px rgba(40,106,148,0.35)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
      boxShadow: "0 2px 6px rgba(40,106,148,0.2)",
    },
  };

  // ---------- Icons Assigned by Button Text ----------
  const buttonIconMap = {
    Create: <HiPlus size={16} />,
    "Search Jobs": <HiSearch size={16} />,
    "Applied Jobs": <HiClipboardList size={16} />,
    "Interview History": <HiClipboardList size={16} />,
    "Team Members Interview": <HiUserGroup size={16} />,
    "Assigned Interviews": <HiClock size={16} />,
    "Add Team Member": <HiUserAdd size={16} />,
    "Confirm Interviews": <HiUserAdd size={16} />,
  };

  // ---------- Reusable Button Renderer ----------
  const renderButton = (label, to, type = "outlined") => (
    <Button
      key={label}
      component={Link}
      to={to}
      variant={type === "contained" ? "contained" : "outlined"}
      size="small"
      sx={
        type === "contained"
          ? premiumBtn
          : {
              borderColor: "#286a94",
              color: "#286a94",
              textTransform: "none",
              fontWeight: 600,
              padding: "6px 12px",
              borderRadius: "8px",
              letterSpacing: "0.5px",
              transition: "all 0.25s ease",
              "&:hover": {
                borderColor: "#1f5676",
                color: "#1f5676",
                background: "#e8f4fb",
              },
            }
      }
    >
      <Box display="flex" alignItems="center" gap="6px">
        {buttonIconMap[label]}
        {label}
      </Box>
    </Button>
  );

  return (
    <Box
      aria-label="breadcrumb"
      sx={{
        px: 2,
        py: 1.5,
        maxWidth: location.pathname.includes("admin") ? "100%" : "95%",
        margin: "auto",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* LEFT SIDE BREADCRUMB */}
      <Breadcrumbs
        separator={
          <NavigateNextIcon fontSize="small" sx={{ color: "#286a94" }} />
        }
        sx={{ color: "#286a94", fontSize: "14px", flexWrap: "wrap" }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return isLast ? (
            <Typography
              key={index}
              sx={{ fontWeight: 600, fontSize: "13px", color: "#0b3c52" }}
            >
              {item.text}
            </Typography>
          ) : typeof item.url === "string" ? (
            <Link
              key={index}
              to={item.url}
              style={{
                textDecoration: "none",
                color: "#286a94",
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {item.text}
            </Link>
          ) : (
            <Typography
              key={index}
              onClick={item.url}
              sx={{
                cursor: "pointer",
                color: "#286a94",
                fontSize: "13px",
              }}
            >
              {item.text}
            </Typography>
          );
        })}
      </Breadcrumbs>

      {/* RIGHT SIDE ACTION BUTTONS */}
      <Stack direction="row" spacing={1}>
        {location.pathname === "/new_job" &&
          candidateVendorId === null &&
          renderButton("Applied Jobs", "/application_status", "contained")}

        {location.pathname === "/schedule-interview" && (
          <>
            {renderButton(
              "Interview History",
              "/interview-history",
              "contained"
            )}
            {renderButton(
              "Team Members Interview",
              "/teamMember-interview",
              "contained"
            )}
          </>
        )}

        {location.pathname === "/posted_jobs" && (
          <>
            {renderButton("Search Jobs", "/all-new-jobs-list", "contained")}
            {renderButton("Create", "/create_job", "contained")}
          </>
        )}

        {userInfo?.vendorId &&
          location.pathname === "/candidate_interviews" && (
            <>
              {renderButton(
                "Assigned Interviews",
                "/assigned_interviews",
                "contained"
              )}
              {/* {renderButton("Confirm Interviews", "/confirmed_interviews","contained")} */}
            </>
          )}
        {location.pathname === "/candidate_interviews" && (
          <>
            {renderButton(
              "Confirm Interviews",
              "/confirmed_interviews",
              "contained"
            )}
          </>
        )}

        {location.pathname === "/interview-approvals" &&
          userResult.usertype === "VENDOR" && (
            <>
              {renderButton(
                "Confirm Interviews",
                "/vendor/confirm-interviews",
                "contained"
              )}
            </>
          )}

        {location.pathname === "/team-list" &&
          renderButton("Add Team Member", "/add-team", "contained")}
      </Stack>
    </Box>
  );
};

export default Breadcrumb;
